import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import Logo from "./components/Logo";
import { Input } from "./components/ui/input";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      navigate("/admin/orders");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "rounded-xl bg-white border-[#D3D7CF] h-12 px-4 text-[#2C402B] placeholder:text-[#576956]/50 focus-visible:ring-2 focus-visible:ring-[#8AA387] focus-visible:ring-offset-0";

  return (
    <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-6" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-[#E8EAE3] rounded-[2.5rem] p-8 sm:p-10 shadow-[0_8px_30px_rgba(44,64,43,0.08)] border border-[#D3D7CF]"
      >
        <div className="flex items-center gap-3 mb-2">
          <Logo size={36} />
          <p className="font-hand text-2xl text-[#C27F67] -rotate-2">els only, shhh</p>
        </div>
        <h1 className="font-heading font-medium tracking-tight text-3xl text-[#2C402B]">Owner login</h1>
        <p className="mt-2 text-sm text-[#576956]">Sign in to peek at your order inbox.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <label htmlFor="login-email" className="block text-sm font-semibold text-[#2C402B] mb-2">Email</label>
            <Input
              id="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@crochetels.com"
              className={inputCls}
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-sm font-semibold text-[#2C402B] mb-2">Password</label>
            <Input
              id="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your secret stitch"
              className={inputCls}
              data-testid="login-password-input"
            />
          </div>
          {error && (
            <p className="text-sm font-semibold text-[#C27F67] bg-[#C27F67]/10 rounded-xl px-4 py-3" data-testid="login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full rounded-full bg-[#2C402B] text-[#F9F6F0] py-4 font-heading font-medium transition-[transform,background-color,box-shadow,opacity] duration-300 hover:bg-[#435E42] hover:scale-[1.02] hover:shadow-[0_14px_30px_rgba(44,64,43,0.25)] disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Open my inbox"}
          </button>
        </form>
        <Link to="/" className="block mt-6 text-center text-sm font-semibold text-[#576956] hover:text-[#2C402B] transition-colors" data-testid="login-back-link">
          Back to the shop
        </Link>
      </motion.div>
    </div>
  );
}
